import html2canvas from "html2canvas";
import type { OfferLine } from "@/hooks/use-offers";
import {
  paginateBudgetLines,
  pngRowMetrics,
  type BudgetPageSlice,
} from "@/lib/offer-export-paginate";

export {
  buildInvoiceTermsText,
  buildInvoiceTermsSentence,
  buildLeftFooterText,
  buildLeftPanelBlocks,
} from "@/lib/offer-export-terms";
export { exportOfferAsPptx } from "@/lib/offer-export-pptx";

/**
 * VIB-664: min/max for auto-skalér. Store budsjett pagineres i lesbar
 * størrelse (12px) i stedet for å krympes mot 7px.
 */
export const OFFER_FONT_SIZE_MAX = 13;
/**
 * VIB-2556: ned fra 11. Malen bruker 8 pt på sitt dobbeltstore lerret,
 * altså ~10,7 px hos oss, så 9–10 px er innenfor malens egen tetthet.
 * Et budsjett på malens størrelse skalerer nå ned i stedet for å bli
 * delt i to bilder, som er den delingen Erik reagerte på.
 */
export const OFFER_FONT_SIZE_MIN = 9;
export const OFFER_FONT_SIZE_PAGE = 10;

function waitForLayoutFlush(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );
}

function isBudgetOverflowing(element: HTMLElement): boolean {
  const panel = element.querySelector<HTMLElement>(
    "[data-budget-panel='root']",
  );
  if (!panel) return false;
  return panel.scrollHeight > panel.clientHeight + 2;
}

/**
 * Prøv 1-kolonne i lesbar størrelse (13→11). To kolonner brukes ikke —
 * malen er én kolonne, og store budsjett får egne 16:9-sider i stedet.
 */
async function findFittingFontSize(
  element: HTMLElement,
  setFontSize: (fs: number) => void,
  setTwoColumns: (b: boolean) => void,
): Promise<{ fontSize: number; twoColumns: boolean; needsPages: boolean }> {
  setTwoColumns(false);
  for (let fs = OFFER_FONT_SIZE_MAX; fs >= OFFER_FONT_SIZE_MIN; fs--) {
    setFontSize(fs);
    await waitForLayoutFlush();
    if (!isBudgetOverflowing(element)) {
      return { fontSize: fs, twoColumns: false, needsPages: false };
    }
  }
  setFontSize(OFFER_FONT_SIZE_PAGE);
  await waitForLayoutFlush();
  return {
    fontSize: OFFER_FONT_SIZE_PAGE,
    twoColumns: false,
    needsPages: true,
  };
}

export async function renderElementToPngBlob(
  element: HTMLElement,
): Promise<Blob> {
  const canvas = await html2canvas(element, {
    backgroundColor: "#020A1F",
    scale: 2,
    useCORS: true,
  });
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/png"),
  );
  if (!blob) throw new Error("Klarte ikke å generere bilde");
  return blob;
}

async function copyPngBlob(blob: Blob): Promise<void> {
  if (!navigator.clipboard || !("write" in navigator.clipboard)) {
    throw new Error("Nettleseren støtter ikke Clipboard API");
  }
  await navigator.clipboard.write([
    new ClipboardItem({ "image/png": blob }),
  ]);
}

export function downloadPngBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export interface CopyImagePaginateOpts {
  visibleLines: OfferLine[];
  setPageSlice: (slice: BudgetPageSlice | null) => void;
  downloadBaseName: string;
}

/**
 * Renderer et HTML-element som PNG og skriver det til utklippstavlen.
 *
 * Store budsjett som ikke får plass i lesbar størrelse splittes i flere
 * 16:9-sider. Da kopieres INGENTING — alle sidene lastes ned i stedet
 * (VIB-2558). Utklippstavlen rommer bare ett bilde, og et halvt budsjett
 * limt inn i Keynote ser komplett ut.
 */
export async function copyElementAsImage(
  element: HTMLElement,
  setFontSize?: (fs: number) => void,
  setTwoColumns?: (b: boolean) => void,
  paginate?: CopyImagePaginateOpts,
): Promise<{ fontSize: number; twoColumns: boolean; pageCount: number }> {
  let fontSize = OFFER_FONT_SIZE_MAX;
  let twoColumns = false;
  let needsPages = false;
  if (setFontSize && setTwoColumns) {
    const result = await findFittingFontSize(
      element,
      setFontSize,
      setTwoColumns,
    );
    fontSize = result.fontSize;
    twoColumns = result.twoColumns;
    needsPages = result.needsPages;
  } else if (setFontSize) {
    setTwoColumns?.(false);
    for (let fs = OFFER_FONT_SIZE_MAX; fs >= OFFER_FONT_SIZE_MIN; fs--) {
      setFontSize(fs);
      await waitForLayoutFlush();
      if (!isBudgetOverflowing(element)) {
        fontSize = fs;
        break;
      }
      fontSize = fs;
    }
    needsPages = isBudgetOverflowing(element);
  }

  if (needsPages && paginate && paginate.visibleLines.length > 0) {
    setTwoColumns?.(false);
    setFontSize?.(OFFER_FONT_SIZE_PAGE);
    const metrics = pngRowMetrics(OFFER_FONT_SIZE_PAGE);
    const pages = paginateBudgetLines(
      paginate.visibleLines,
      metrics.row,
      metrics.phase,
      metrics.max,
    );
    const blobs: Blob[] = [];
    for (let i = 0; i < pages.length; i++) {
      paginate.setPageSlice({
        lines: pages[i],
        pageNumber: i + 1,
        pageTotal: pages.length,
        showTotals: i === pages.length - 1,
      });
      await waitForLayoutFlush();
      blobs.push(await renderElementToPngBlob(element));
    }
    paginate.setPageSlice(null);
    await waitForLayoutFlush();
    if (blobs.length > 1) {
      // VIB-2558: utklippstavlen holder bare ett bilde. Å legge side 1
      // der ga et halvt budsjett som så komplett ut når det ble limt inn.
      // Deles budsjettet, laster vi bare ned alle sidene.
      blobs.forEach((blob, i) => {
        downloadPngBlob(
          blob,
          `${paginate.downloadBaseName}_side_${i + 1}.png`,
        );
      });
    } else {
      await copyPngBlob(blobs[0]);
    }
    return {
      fontSize: OFFER_FONT_SIZE_PAGE,
      twoColumns: false,
      pageCount: pages.length,
    };
  }

  const blob = await renderElementToPngBlob(element);
  await copyPngBlob(blob);
  return { fontSize, twoColumns, pageCount: 1 };
}
